import { cloneDeep, get, merge, set } from 'lodash'

type TrackingPayload = Record<string, unknown>

type AnalyticsImplementation = {
  default: TrackingPayload
  datalayer: TrackingPayload
  pageName: string
  isLive?: boolean
  pageBottom: () => void
  setDefaults: (obj: TrackingPayload) => void
  getFromDatalayer: (key: string) => unknown
  addToDatalayer: (additions: TrackingPayload) => void
  setTrackingData: (additions: TrackingPayload) => void
  trackViewChange: () => void
  trackPage: () => void
  trackInteraction: () => void
  track: (action: string) => void
  keys: {
    formType: () => string
    formName: () => string
    errorFields: () => string
    lastTouchedField: () => string
    pageName: () => string
    viewChange: () => string
    allowPostalAds: () => string
    allowPhoneAds: () => string
    allowElectronicAds: () => string
    allowMailAds: () => string
    allowElectronicInvoices: () => string
    eventType: (eventCount: number) => string
    eventAction: (eventCount: number) => string
    eventTargetURL: (eventCount: number) => string
    linkInformation: (eventCount: number) => string
    errorCode: () => string
    errorMessage: () => string
    errorCausingURL: () => string
  }
}

const mock: AnalyticsImplementation = {
  default: {},
  datalayer: {},
  pageName: '',
  pageBottom: function pageBottom() {},

  setDefaults: function setDefaults(obj) {
    Object.assign(this.default, obj)
  },
  getFromDatalayer(key) {
    return get(this.datalayer, key)
  },
  addToDatalayer(additions) {
    Object.keys(additions).forEach((key) => {
      set(this.datalayer, key, additions[key])
    })
  },

  setTrackingData(additions) {
    const clonedDefaultDatalayer = cloneDeep(this.default)
    const trackingData: TrackingPayload = {}

    Object.keys(additions).forEach((key) => {
      set(trackingData, key, additions[key])
    })

    window.du_digitalData = merge({}, clonedDefaultDatalayer, this.datalayer, trackingData)
  },
  trackViewChange: function viewChange() {
    this.track('viewChange')
  },

  trackPage: function page() {
    this.track('page')
  },

  trackInteraction: function interaction() {
    this.track('interaction')
  },

  track: function track(action) {
    if (typeof action !== 'string') {
      console.warn('Legacy tracking code detected')
      return
    }

    if (!implementation.isLive || window.__CJ_DEBUG_ANALYTICS) {
      console.log(
        `track ${action}\n pageName: ${get(
          window.du_digitalData,
          this.keys.pageName()
        )}\n viewChange: ${get(window.du_digitalData, this.keys.viewChange())}\n event0.type: ${get(
          window.du_digitalData,
          this.keys.eventType(0)
        )}\n event0.action: ${get(window.du_digitalData, this.keys.eventAction(0))}\n\n Datalayer:`,
        window.du_digitalData
      )
    }

    window._satellite?.track(action)
  },
  keys: {
    formType: () => 'form.type',
    formName: () => 'form.name',
    errorFields: () => 'form.errorFields',
    lastTouchedField: () => 'form.lastTouchedField',
    pageName: () => 'core.pageInfo.pageName',
    viewChange: () => 'core.attributes.viewChange',
    allowPostalAds: () => 'dataPrivacyStatement.allowPostalAds',
    allowPhoneAds: () => 'dataPrivacyStatement.allowPhoneAds',
    allowElectronicAds: () => 'dataPrivacyStatement.allowElectronicAds',
    allowMailAds: () => 'dataPrivacyStatement.allowMailAds',
    allowElectronicInvoices: () => 'dataPrivacyStatement.allowElectronicInvoices',
    eventType: (eventCount: number) => `event[${eventCount}].eventInfo.eventType`,
    eventAction: (eventCount: number) => `event[${eventCount}].eventInfo.eventAction`,
    eventTargetURL: (eventCount: number) => `event[${eventCount}].eventInfo.eventTargetURL`,
    linkInformation: (eventCount: number) => `event[${eventCount}].eventInfo.linkInformation`,
    errorCode: () => 'error.errorCode',
    errorMessage: () => 'error.errorMessage',
    errorCausingURL: () => 'error.errorCausingURL'
  }
}

const real: AnalyticsImplementation = {
  ...mock,
  isLive: true,
  pageBottom: function pageBottom() {
    window._satellite?.pageBottom()
  }
}

let implementation: AnalyticsImplementation = mock

if (process.env.REACT_APP_ANALYTICS) {
  implementation = real
}

export { implementation as Analytics }
